import React, { ReactElement, ComponentProps } from 'react';
import { useJakhubTranslation } from '../../../../i18n/jakhub-translation';

import BannerFiller, { Card } from './banner-filler';
import BannerVideo from './banner-video';
import StyledContent from '../../styled-content/styled-content';

import styles from './banner-v2.module.scss';

export type { Card };

type BannerTextTypes =
    | 'bannerV2Title'
    | 'bannerV2SubTitle'
    | 'bannerV2Description'
    | 'bannerV2LongDescription';
type BannerTextProps = {
    type: BannerTextTypes;
    dictionaryBase: string;
    supportingObject: any;
    lightText: boolean;
    slim: boolean;
};
function BannerText(props: BannerTextProps) {
    const { tt, T } = useJakhubTranslation();
    const { dictionaryBase, supportingObject, type, lightText, slim } = props;

    const dictionaryText: BannerTextKeys = tt(dictionaryBase);
    const extractedText = dictionaryText[type];
    const textStyle = `
        ${styles[type]}
        ${slim ? styles[`${type}_slim`] : ''}
        ${lightText ? styles[`${type}_light`] : ''}
    `;
    return extractedText ? (
        <span className={textStyle}>{T(extractedText, supportingObject)}</span>
    ) : null;
}

type Props = {
    id?: string;
    imageUrl?: string;
    videoUrl?: string;
    cards?: Card[];
    dictionaryBase: string;
    lightText?: boolean;
    slim?: boolean;
    noVeil?: boolean;
    minHeight?: number;
    supportingObject?: any;
    ctasComponent?: ReactElement;
    bottomComponent?: ReactElement;
    stickyBottomComponent?: ReactElement;
} & ComponentProps<'div'>;
type BannerTextKeys = {
    bannerV2SectionTitle: string;
    bannerV2Title: string;
    bannerV2SubTitle: string;
    bannerV2Description: string;
    bannerV2LongDescription: string;
};

type Patate = {
    imageUrl?: string;
    videoUrl?: string;
    cards?: Card[];
    minHeight?: number;
    noVeil: boolean;
} & ComponentProps<'div'>;
function BackGroundDiv(props: Patate) {
    const { children, noVeil, imageUrl, videoUrl, cards, minHeight } = props;

    if (videoUrl) {
        return (
            <>
                <div className={styles.bannerVideo}>
                    <BannerVideo videoUrl={videoUrl} controls={false} />
                    {!noVeil && <div className={styles.bannerVideoVeil} />}
                    <div className={styles.bannerVideoChildren}>{children}</div>
                </div>
            </>
        );
    }

    if (cards) return <BannerFiller cards={cards}>{children}</BannerFiller>;

    if (imageUrl) {
        return (
            <div
                style={{
                    backgroundImage: `url(${imageUrl})`,
                    minHeight: `${minHeight ? `${minHeight}px` : null}`,
                }}
                className={styles.bannerHeader}
            >
                {children}
            </div>
        );
    }

    return <div style={{ minHeight: `${minHeight ? `${minHeight}px` : null}` }}>{children}</div>;
}

export default function BannerV2(props: Props) {
    const { tt } = useJakhubTranslation();
    const {
        children,
        dictionaryBase,
        supportingObject,
        imageUrl,
        videoUrl,
        cards,
        ctasComponent,
        bottomComponent,
        stickyBottomComponent,
        lightText = false,
        slim = false,
        minHeight,
        noVeil = false,
    } = props;

    const bannerText: BannerTextKeys = tt(dictionaryBase);

    const textKeys = [
        'bannerV2Title',
        'bannerV2SubTitle',
        'bannerV2Description',
        'bannerV2LongDescription',
    ];
    const { bannerV2Title, bannerV2SubTitle, bannerV2Description } = bannerText;
    const ariaLabel = [bannerV2Title, bannerV2SubTitle, bannerV2Description].join(' ');

    return (
        <BackGroundDiv
            imageUrl={imageUrl}
            videoUrl={videoUrl}
            minHeight={minHeight}
            cards={cards}
            noVeil={noVeil}
        >
            <StyledContent contentStyle='HEADER'>
                <StyledContent contentStyle='PADDED'>
                    <span role='img' aria-label={`${ariaLabel}`} style={{ width: 0 }} />
                    <div className={`${styles.bannerBody} ${slim ? styles.bannerBody_slim : ''}`}>
                        <div className={styles.bannerTextContainer}>
                            {textKeys.map((textKey) => (
                                <BannerText
                                    slim={slim}
                                    lightText={!!cards || lightText || !!videoUrl}
                                    key={textKey}
                                    type={textKey as BannerTextTypes}
                                    dictionaryBase={dictionaryBase}
                                    supportingObject={supportingObject}
                                />
                            ))}
                        </div>
                        {ctasComponent ? ctasComponent : null}
                        {children}
                    </div>
                    {bottomComponent ? (
                        <div className={styles.bannerBottom}>{bottomComponent}</div>
                    ) : null}
                </StyledContent>
            </StyledContent>
            {stickyBottomComponent ? (
                <div className={styles.bannerStickyBottom}>{stickyBottomComponent}</div>
            ) : null}
        </BackGroundDiv>
    );
}
